import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../lib/firebase/config';
import FormInput from '../components/auth/FormInput';

const deleteAccountSchema = z.object({
  password: z.string().min(8, 'Password is required'),
  feedback: z.string().max(1000, 'Feedback is too long').optional(),
  confirm: z.literal(true, {
    errorMap: () => ({ message: 'You must confirm account deletion.' }),
  }),
});

export default function DeleteAccount() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(deleteAccountSchema),
    defaultValues: { confirm: false },
  });

  const onSubmit = async (data) => {
    setIsLoading(true);
    setError('');
    setIsSuccess(false);
    try {
      const user = auth.currentUser;
      if (!user || !user.email) {
        setError('No authenticated user found.');
        console.error('[DeleteAccount] No authenticated user found.');
        setIsLoading(false);
        return;
      }
      // Re-authenticate user
      const { EmailAuthProvider, reauthenticateWithCredential } = await import('firebase/auth');
      const credential = EmailAuthProvider.credential(user.email, data.password);
      console.log('[DeleteAccount] Reauthenticating user:', user.email);
      await reauthenticateWithCredential(user, credential);
      // Call Cloud Function to delete user and anonymize data
      const functions = getFunctions();
      const deleteUserFn = httpsCallable(functions, 'deleteUserWithAnonymisedExitFeedback');
      console.log('[DeleteAccount] Calling deleteUserWithAnonymisedExitFeedback Cloud Function.');
      await deleteUserFn({ feedbackText: data.feedback || '' });
      setIsSuccess(true);
      reset();
      console.log('[DeleteAccount] Account deleted successfully.');
      setTimeout(() => {
        window.location.href = '/goodbye';
      }, 3000);
    } catch (err) {
      let message = 'An error occurred. Please try again.';
      if (err.code === 'auth/wrong-password') {
        message = 'Password is incorrect.';
      } else if (err.code === 'auth/too-many-requests') {
        message = 'Too many failed attempts. Please try again later.';
      } else if (err.code === 'auth/requires-recent-login') {
        message = 'Please log in again and try deleting your account.';
      } else if (err.message) {
        message = err.message;
      }
      setError(message);
      console.error('[DeleteAccount] Error deleting account:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Account Deleted</h2>
        <div className="rounded-md bg-green-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Your account has been deleted. We're sorry to see you go.
              </h3>
              <div className="mt-2 text-sm text-green-700">
                <p>You will be redirected shortly.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-red-700 mb-6">Delete Account</h2>
      <div className="rounded-md bg-red-50 p-4 mb-6">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Warning: This action is permanent!</h3>
            <div className="mt-2 text-sm text-red-700">
              <ul className="list-disc pl-5 space-y-1">
                <li>Your account and all associated data will be permanently deleted.</li>
                <li>This action cannot be undone.</li>
                <li>You will lose access to all Orrekie Platform services.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label htmlFor="feedback" className="block text-sm font-medium text-gray-700">
            (Optional) Please let us know why you're leaving:
          </label>
          <textarea
            id="feedback"
            rows={3}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orrekie-500 focus:ring-orrekie-500 sm:text-sm"
            placeholder="Your feedback..."
            {...register('feedback')}
          />
          {errors.feedback && (
            <p className="mt-2 text-sm text-red-600">{errors.feedback.message}</p>
          )}
        </div>
        <FormInput
          label="Confirm your password"
          id="password"
          type="password"
          autoComplete="current-password"
          error={errors.password?.message}
          {...register('password')}
        />
        <div className="flex items-center">
          <input
            id="confirm"
            type="checkbox"
            {...register('confirm')}
            className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
          />
          <label htmlFor="confirm" className="ml-2 block text-sm text-gray-700">
            I understand this action cannot be undone
          </label>
        </div>
        {errors.confirm && (
          <p className="mt-2 text-sm text-red-600">{errors.confirm.message}</p>
        )}
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{error}</h3>
              </div>
            </div>
          </div>
        )}
        <div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Deleting account...' : 'Delete my account permanently'}
          </button>
        </div>
      </form>
    </div>
  );
} 